import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Play, RotateCcw } from 'lucide-react';
import { useAcademyStore } from '../../../store/academyStore';
import { ModuleSuccess } from '../../ui/ModuleSuccess';
import { LessonWrapper, LessonPage } from '../../ui/LessonWrapper';

// ============ LESSON CONTENT ============
const LESSONS: LessonPage[] = [
    {
        id: 1,
        title: "Speaking Computer",
        icon: "🗣",
        content: (
            <div className="space-y-6">
                <div className="bg-purple-50 p-6 rounded-2xl border-2 border-purple-200">
                    <h3 className="font-bold text-xl text-purple-800 mb-3">Algorithms</h3>
                    <p className="text-slate-700 mb-4">
                        Computers aren't smart. They just do <strong>exactly</strong> what you tell them.
                    </p>
                    <p className="text-slate-700">
                        An <strong>Algorithm</strong> is a list of steps to solve a problem. Like a recipe!
                    </p>
                </div>
            </div>
        )
    },
    {
        id: 2,
        title: "The Blocks",
        icon: "🧱",
        content: (
            <div className="space-y-6">
                <ul className="space-y-4">
                    <li className="flex items-center gap-4 bg-blue-50 p-4 rounded-xl border border-blue-200">
                        <div className="bg-blue-500 text-white px-3 py-1 rounded font-bold">Move</div>
                        <span className="text-slate-600">Walks forward one square.</span>
                    </li>
                    <li className="flex items-center gap-4 bg-green-50 p-4 rounded-xl border border-green-200">
                        <div className="bg-green-500 text-white px-3 py-1 rounded font-bold">Turn</div>
                        <span className="text-slate-600">Rotates 90 degrees (Left or Right).</span>
                    </li>
                    <li className="flex items-center gap-4 bg-purple-50 p-4 rounded-xl border border-purple-200">
                        <div className="bg-purple-500 text-white px-3 py-1 rounded font-bold">Repeat</div>
                        <span className="text-slate-600">Loops the next 2 blocks multiple times.</span>
                    </li>
                </ul>
            </div>
        )
    },
    {
        id: 3,
        title: "Mission Brief",
        icon: "🦍",
        content: (
            <div className="space-y-6">
                <div className="bg-yellow-50 p-6 rounded-2xl border-2 border-yellow-200">
                    <h3 className="font-bold text-xl text-yellow-800 mb-3">Get the Banana!</h3>
                    <p className="text-slate-700 mb-4">
                        Guide the Gorilla to the Banana.
                        <strong>Complete all 3 levels</strong> to pass the module.
                    </p>
                    <p className="font-bold text-yellow-900">
                        Think step-by-step!
                    </p>
                </div>
            </div>
        )
    }
];

// ============ GAME LOGIC ============
interface Block {
    id: string;
    type: 'move' | 'turn' | 'repeat' | 'if';
    value?: number | string;
    color: string;
    label: string;
}

const AVAILABLE_BLOCKS: Block[] = [
    { id: 'move', type: 'move', value: 1, color: 'bg-blue-500', label: '➡️ Move Forward' },
    { id: 'turn-right', type: 'turn', value: 'right', color: 'bg-green-500', label: '↩️ Turn Right' },
    { id: 'turn-left', type: 'turn', value: 'left', color: 'bg-green-500', label: '↪️ Turn Left' },
    { id: 'repeat-2', type: 'repeat', value: 2, color: 'bg-purple-500', label: '🔁 Repeat 2x' },
];

const LEVELS = [
    {
        id: 1,
        name: 'First Steps',
        goal: 'Get the gorilla to the banana!',
        grid: [
            ['G', '.', '.', 'B'],
        ],
    },
    {
        id: 2,
        name: 'Turn Around',
        goal: 'Go around the corner!',
        grid: [
            ['G', '.', '.'],
            ['.', '.', '.'],
            ['.', '.', 'B'],
        ],
    },
    {
        id: 3,
        name: 'Loop Master',
        goal: 'Use repeat to save blocks!',
        grid: [
            ['G', '.', '.', '.', '.', 'B'],
        ],
    },
];

const CodingGame = ({ onComplete }: { onComplete: () => void }) => {
    const [level, setLevel] = useState(0);
    const [program, setProgram] = useState<Block[]>([]);
    const [isRunning, setIsRunning] = useState(false);
    const [gorillaPos, setGorillaPos] = useState({ x: 0, y: 0 });
    const [gorillaDir, setGorillaDir] = useState(0); // 0=right, 1=down, 2=left, 3=up
    const [won, setWon] = useState(false);
    const [crashed, setCrashed] = useState(false);

    const currentLevel = LEVELS[level];

    // Initialize/Reset Position when level changes
    useEffect(() => {
        resetPosition();
    }, [level]);

    const resetPosition = () => {
        const grid = currentLevel.grid;
        for (let y = 0; y < grid.length; y++) {
            for (let x = 0; x < grid[y].length; x++) {
                if (grid[y][x] === 'G') {
                    setGorillaPos({ x, y });
                    setGorillaDir(0);
                    return;
                }
            }
        }
    };

    const addBlock = (block: Block) => {
        if (program.length < 8) {
            setProgram([...program, { ...block, id: `${block.id}-${Date.now()}` }]);
        }
    };

    const removeBlock = (index: number) => {
        setProgram(program.filter((_, i) => i !== index));
    };

    const runProgram = async () => {
        setIsRunning(true);
        setWon(false);
        setCrashed(false);

        // Reset visible state first
        let pos = { ...gorillaPos };
        let dir = 0;
        const grid = currentLevel.grid;

        // Reset to initial before running
        for (let y = 0; y < grid.length; y++) {
            for (let x = 0; x < grid[y].length; x++) {
                if (grid[y][x] === 'G') {
                    pos = { x, y };
                    setGorillaPos(pos);
                    setGorillaDir(0);
                    break;
                }
            }
        }
        await new Promise(r => setTimeout(r, 400));

        const executeBlock = async (block: Block): Promise<boolean> => {
            if (block.type === 'move') {
                const dx = dir === 0 ? 1 : dir === 2 ? -1 : 0;
                const dy = dir === 1 ? 1 : dir === 3 ? -1 : 0;
                const newPos = { x: pos.x + dx, y: pos.y + dy };

                // Check Bounds
                if (
                    newPos.y < 0 || newPos.y >= grid.length ||
                    newPos.x < 0 || newPos.x >= grid[0].length
                ) {
                    setCrashed(true);
                    return false;
                }

                pos = newPos;
                setGorillaPos({ ...pos });
            } else if (block.type === 'turn') {
                if (block.value === 'right') dir = (dir + 1) % 4;
                else dir = (dir + 3) % 4;
                setGorillaDir(dir);
            }
            await new Promise(r => setTimeout(r, 400));
            return true;
        };

        for (const block of program) {
            if (crashed) break;

            if (block.type === 'repeat') {
                const repeatCount = block.value as number;
                const nextBlocks = program.slice(program.indexOf(block) + 1, program.indexOf(block) + 3);
                for (let i = 0; i < repeatCount; i++) {
                    for (const b of nextBlocks) {
                        const success = await executeBlock(b);
                        if (!success) {
                            setIsRunning(false);
                            return;
                        }
                    }
                }
            } else {
                const success = await executeBlock(block);
                if (!success) {
                    setIsRunning(false);
                    return;
                }
            }
        }

        // Check Win
        const goalY = grid.findIndex(row => row.includes('B'));
        const goalX = grid[goalY]?.indexOf('B') ?? -1;

        if (pos.x === goalX && pos.y === goalY) {
            setWon(true);
        }

        setIsRunning(false);
    };

    const nextLevel = () => {
        if (level < LEVELS.length - 1) {
            setLevel(l => l + 1);
            setProgram([]);
            setWon(false);
        } else {
            onComplete();
        }
    };

    return (
        <div className="space-y-6">
            <div className="bg-white rounded-2xl p-4 flex justify-between items-center shadow-lg border border-purple-100">
                <div>
                    <div className="font-bold text-purple-600">Level {level + 1}: {currentLevel.name}</div>
                    <div className="text-slate-500 text-sm">{currentLevel.goal}</div>
                </div>
                <div className="text-sm text-slate-400">
                    {level + 1}/{LEVELS.length}
                </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                {/* Game Grid */}
                <div className="bg-white rounded-3xl p-6 shadow-xl border-2 border-slate-100">
                    <div className="flex justify-center mb-6 min-h-[200px] items-center">
                        <div className="inline-block bg-green-100 rounded-2xl p-4 border-4 border-green-300">
                            {currentLevel.grid.map((row, y) => (
                                <div key={y} className="flex">
                                    {row.map((cell, x) => (
                                        <div
                                            key={x}
                                            className={`w-16 h-16 border-2 border-green-200 flex items-center justify-center text-3xl ${cell === 'B' ? 'bg-yellow-100' : 'bg-green-50'}`}
                                        >
                                            {gorillaPos.x === x && gorillaPos.y === y ? (
                                                <motion.div
                                                    animate={{ rotate: gorillaDir * 90 }}
                                                    className="text-4xl"
                                                >
                                                    🦍
                                                </motion.div>
                                            ) : cell === 'B' ? '🍌' : ''}
                                        </div>
                                    ))}
                                </div>
                            ))}
                        </div>
                    </div>

                    <div className="flex justify-center gap-4">
                        <button
                            onClick={runProgram}
                            disabled={isRunning || program.length === 0}
                            className="bg-green-500 text-white px-6 py-3 rounded-xl font-bold hover:bg-green-600 disabled:opacity-50 flex items-center gap-2"
                        >
                            <Play size={18} /> Run!
                        </button>
                        <button
                            onClick={() => { setProgram([]); resetPosition(); setWon(false); setCrashed(false); }}
                            className="bg-slate-200 text-slate-600 px-6 py-3 rounded-xl font-bold hover:bg-slate-300 flex items-center gap-2"
                        >
                            <RotateCcw size={18} /> Reset
                        </button>
                    </div>

                    {crashed && (
                        <div className="mt-4 text-center text-red-600 font-bold bg-red-50 p-3 rounded-xl border border-red-200 animate-bounce">
                            💥 Ouch! Hit a wall!
                        </div>
                    )}

                    {won && (
                        <div className="mt-4 text-center">
                            <motion.button
                                initial={{ scale: 0 }}
                                animate={{ scale: 1 }}
                                onClick={nextLevel}
                                className="bg-purple-500 text-white px-8 py-3 rounded-xl font-bold hover:bg-purple-600 shadow-lg animate-pulse"
                            >
                                {level < LEVELS.length - 1 ? "Next Level →" : "Finish Module! 🎉"}
                            </motion.button>
                        </div>
                    )}
                </div>

                {/* Code Area */}
                <div className="space-y-6">
                    <div className="bg-white rounded-3xl p-6 shadow-xl border-2 border-slate-100">
                        <h3 className="font-bold text-slate-700 mb-4">🧱 Blocks (Click to Add)</h3>
                        <div className="grid grid-cols-2 gap-3">
                            {AVAILABLE_BLOCKS.map(block => (
                                <button
                                    key={block.id}
                                    onClick={() => addBlock(block)}
                                    className={`${block.color} text-white p-3 rounded-xl font-bold text-sm hover:opacity-90 transition-all shadow-lg active:scale-95`}
                                >
                                    {block.label}
                                </button>
                            ))}
                        </div>
                    </div>

                    <div className="bg-slate-800 rounded-3xl p-6 shadow-xl min-h-[200px]">
                        <h3 className="font-bold text-white mb-4">📝 Your Program</h3>
                        <div className="space-y-2">
                            <AnimatePresence>
                                {program.map((block, i) => (
                                    <motion.div
                                        key={block.id}
                                        initial={{ x: -20, opacity: 0 }}
                                        animate={{ x: 0, opacity: 1 }}
                                        exit={{ x: 20, opacity: 0 }}
                                        className={`${block.color} text-white p-3 rounded-xl font-bold flex justify-between items-center`}
                                    >
                                        <span>{block.label}</span>
                                        <button onClick={() => removeBlock(i)} className="bg-white/20 px-2 py-1 rounded text-xs hover:bg-white/30">✕</button>
                                    </motion.div>
                                ))}
                            </AnimatePresence>
                            {program.length === 0 && (
                                <div className="text-slate-500 text-center py-8 italic">
                                    Click blocks above to add them here!
                                </div>
                            )}
                        </div>
                        <div className="mt-4 text-xs text-slate-500 text-right">{program.length}/8 blocks</div>
                    </div>
                </div>
            </div>
        </div>
    );
};

// ============ MAIN COMPONENT ============
export const VisualCoding = () => {
    const { completeModule } = useAcademyStore();
    const [completed, setCompleted] = useState(false);

    const handleComplete = () => {
        setCompleted(true);
        completeModule('visual-coding', 100);
    };

    return (
        <LessonWrapper
            moduleName="Visual Coding"
            moduleEmoji="🧩"
            moduleColor="purple"
            lessons={LESSONS}
            completed={completed}
            activityLabel="Game"
        >
            <CodingGame onComplete={handleComplete} />
        </LessonWrapper>
    );
};

export default VisualCoding;
